import { SUBTITLE_DRAG_HANDLE_CLASS } from './subtitlePositionDrag.ts';

export const SHADOWING_HANDLE_LANE_REVEAL_CLASS = 'is-lane-reveal';

const LANE_PAD_Y_PX = 10;

export type RectLike = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

/**
 * True when the pointer is within the subtitle track horizontally and within
 * the drag handle's vertical band (including a small pad). Display-only —
 * does not create a hit target for dragging.
 */
export function isPointerInShadowingHandleLane(
  clientX: number,
  clientY: number,
  handleRect: RectLike,
  containerRect: RectLike,
  padYPx = LANE_PAD_Y_PX,
): boolean {
  const inX = clientX >= containerRect.left && clientX <= containerRect.right;
  const inY = clientY >= handleRect.top - padYPx && clientY <= handleRect.bottom + padYPx;
  return inX && inY;
}

export type ShadowingHandleLaneRevealDeps = {
  isShadowingMode: () => boolean;
};

/**
 * Shadowing only: reveal the position grip when the cursor is anywhere on the
 * same horizontal lane (full track width). Sides are not operable — no extra
 * pointer target; other modes keep handle-local hover.
 */
export function installShadowingHandleLaneReveal(
  deps: ShadowingHandleLaneRevealDeps,
): () => void {
  let raf = 0;
  let lastX = 0;
  let lastY = 0;

  const clearAll = () => {
    document.querySelectorAll(`.${SUBTITLE_DRAG_HANDLE_CLASS}.${SHADOWING_HANDLE_LANE_REVEAL_CLASS}`)
      .forEach((node) => node.classList.remove(SHADOWING_HANDLE_LANE_REVEAL_CLASS));
  };

  const sync = () => {
    raf = 0;
    if (!deps.isShadowingMode()) {
      clearAll();
      return;
    }
    const handles = document.querySelectorAll(
      `.unisubs-dubbing .${SUBTITLE_DRAG_HANDLE_CLASS}`,
    );
    if (handles.length === 0) {
      clearAll();
      return;
    }
    handles.forEach((node) => {
      const handle = node as HTMLElement;
      const container = handle.closest('#mainSub, #secondSub') as HTMLElement | null;
      if (!container) {
        handle.classList.remove(SHADOWING_HANDLE_LANE_REVEAL_CLASS);
        return;
      }
      const reveal = isPointerInShadowingHandleLane(
        lastX,
        lastY,
        handle.getBoundingClientRect(),
        container.getBoundingClientRect(),
      );
      handle.classList.toggle(SHADOWING_HANDLE_LANE_REVEAL_CLASS, reveal);
    });
  };

  const onPointerMove = (event: PointerEvent) => {
    lastX = event.clientX;
    lastY = event.clientY;
    if (raf) return;
    raf = requestAnimationFrame(sync);
  };

  const onBlur = () => clearAll();

  window.addEventListener('pointermove', onPointerMove, { passive: true });
  window.addEventListener('blur', onBlur);
  return () => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('blur', onBlur);
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    clearAll();
  };
}
